import { useState, useEffect, useRef } from 'react'

interface TypewriterTextProps {
  fullText: string
  onComplete?: () => void
  onUpdate?: () => void // Called after each chunk update for auto-scroll
  chunkSize?: number // default 3
  delay?: number // default 15ms
  showCursor?: boolean // default true
  children: (displayedText: string, isStreaming: boolean) => React.ReactNode
}

export default function TypewriterText({
  fullText,
  onComplete,
  onUpdate,
  chunkSize = 3,
  delay = 15,
  showCursor = true,
  children
}: TypewriterTextProps) {
  const [displayedLength, setDisplayedLength] = useState(0)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isCompleteRef = useRef(false)
  const onCompleteRef = useRef(onComplete)
  const onUpdateRef = useRef(onUpdate)

  // Keep refs updated
  useEffect(() => {
    onCompleteRef.current = onComplete
    onUpdateRef.current = onUpdate
  }, [onComplete, onUpdate])

  // Handle completion detection separately to avoid setState during render
  // Use a separate effect that runs after render to check completion
  useEffect(() => {
    // Only check completion after render cycle completes
    if (displayedLength >= fullText.length && fullText.length > 0 && !isCompleteRef.current) {
      isCompleteRef.current = true
      // Defer callback to next event loop to ensure render is complete
      const timeoutId = setTimeout(() => {
        onCompleteRef.current?.()
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [displayedLength, fullText.length])

  useEffect(() => {
    // Reset when fullText changes
    setDisplayedLength(0)
    isCompleteRef.current = false
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    // Skip if no text
    if (fullText.length === 0) {
      return
    }

    // Stream next chunk
    const streamNext = () => {
      setDisplayedLength(prev => {
        const nextLength = Math.min(prev + chunkSize, fullText.length)
        
        if (nextLength >= fullText.length) {
          return fullText.length
        }
        
        // Schedule next chunk
        timeoutRef.current = setTimeout(streamNext, delay)
        return nextLength
      })
      
      // Defer onUpdate to avoid setState during render
      requestAnimationFrame(() => {
        onUpdateRef.current?.()
      })
    }

    // Start streaming
    timeoutRef.current = setTimeout(streamNext, delay)

    // Cleanup on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [fullText, chunkSize, delay])

  const displayedText = fullText.substring(0, displayedLength)
  const isStreaming = displayedLength < fullText.length

  return (
    <>
      {children(displayedText, isStreaming)}
      {showCursor && isStreaming && (
        <span 
          className="typewriter-cursor" 
          style={{
            animation: 'blink 1s infinite',
            marginLeft: '2px',
            color: 'inherit'
          }}
        >|</span>
      )}
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </>
  )
}
